import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle, MapPin, AlertTriangle } from "lucide-react";
import { useGeoLocation } from "@/contexts/LocationContext";
import { useQuery } from "@tanstack/react-query";
import { normalizeCityName, slugifyCity } from "@/lib/cityNormalization";
import {
  sanitizeCityName,
  isValidCityName,
  sanitizeEmail,
  isValidEmail,
  checkRateLimit,
} from "@/lib/inputSanitization";

// Complete world country list — ISO 3166-1 alpha-2
const ALL_COUNTRIES: { code: string; name: string; flag: string }[] = [
  { code: "NL", name: "Nederland", flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "BE", name: "Belgi\u00EB", flag: "\u{1F1E7}\u{1F1EA}" },
  { code: "DE", name: "Duitsland", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "GB", name: "United Kingdom", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "FR", name: "France", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "ES", name: "Espa\u00F1a", flag: "\u{1F1EA}\u{1F1F8}" },
  { code: "IT", name: "Italia", flag: "\u{1F1EE}\u{1F1F9}" },
  { code: "PT", name: "Portugal", flag: "\u{1F1F5}\u{1F1F9}" },
  { code: "AT", name: "\u00D6sterreich", flag: "\u{1F1E6}\u{1F1F9}" },
  { code: "CH", name: "Schweiz", flag: "\u{1F1E8}\u{1F1ED}" },
  { code: "SE", name: "Sverige", flag: "\u{1F1F8}\u{1F1EA}" },
  { code: "NO", name: "Norge", flag: "\u{1F1F3}\u{1F1F4}" },
  { code: "DK", name: "Danmark", flag: "\u{1F1E9}\u{1F1F0}" },
  { code: "FI", name: "Suomi", flag: "\u{1F1EB}\u{1F1EE}" },
  { code: "PL", name: "Polska", flag: "\u{1F1F5}\u{1F1F1}" },
  { code: "CZ", name: "\u010Ce\u0161ko", flag: "\u{1F1E8}\u{1F1FF}" },
  { code: "US", name: "United States", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "CA", name: "Canada", flag: "\u{1F1E8}\u{1F1E6}" },
  { code: "AU", name: "Australia", flag: "\u{1F1E6}\u{1F1FA}" },
  { code: "BR", name: "Brasil", flag: "\u{1F1E7}\u{1F1F7}" },
  { code: "TR", name: "T\u00FCrkiye", flag: "\u{1F1F9}\u{1F1F7}" },
  { code: "IE", name: "Ireland", flag: "\u{1F1EE}\u{1F1EA}" },
  { code: "HR", name: "Hrvatska", flag: "\u{1F1ED}\u{1F1F7}" },
  { code: "SI", name: "Slovenija", flag: "\u{1F1F8}\u{1F1EE}" },
  { code: "RO", name: "Rom\u00E2nia", flag: "\u{1F1F7}\u{1F1F4}" },
  { code: "HU", name: "Magyarorsz\u00E1g", flag: "\u{1F1ED}\u{1F1FA}" },
  { code: "GR", name: "\u0395\u03BB\u03BB\u03AC\u03B4\u03B1", flag: "\u{1F1EC}\u{1F1F7}" },
  { code: "SR", name: "Suriname", flag: "\u{1F1F8}\u{1F1F7}" },
  { code: "AW", name: "Aruba", flag: "\u{1F1E6}\u{1F1FC}" },
  { code: "CW", name: "Cura\u00E7ao", flag: "\u{1F1E8}\u{1F1FC}" },
  { code: "SX", name: "Sint Maarten", flag: "\u{1F1F8}\u{1F1FD}" },
  { code: "MX", name: "M\u00E9xico", flag: "\u{1F1F2}\u{1F1FD}" },
  { code: "AR", name: "Argentina", flag: "\u{1F1E6}\u{1F1F7}" },
  { code: "CO", name: "Colombia", flag: "\u{1F1E8}\u{1F1F4}" },
  { code: "CL", name: "Chile", flag: "\u{1F1E8}\u{1F1F1}" },
  { code: "PE", name: "Per\u00FA", flag: "\u{1F1F5}\u{1F1EA}" },
  { code: "ZA", name: "South Africa", flag: "\u{1F1FF}\u{1F1E6}" },
  { code: "NG", name: "Nigeria", flag: "\u{1F1F3}\u{1F1EC}" },
  { code: "KE", name: "Kenya", flag: "\u{1F1F0}\u{1F1EA}" },
  { code: "IN", name: "India", flag: "\u{1F1EE}\u{1F1F3}" },
  { code: "PH", name: "Philippines", flag: "\u{1F1F5}\u{1F1ED}" },
  { code: "ID", name: "Indonesia", flag: "\u{1F1EE}\u{1F1E9}" },
  { code: "SG", name: "Singapore", flag: "\u{1F1F8}\u{1F1EC}" },
  { code: "MY", name: "Malaysia", flag: "\u{1F1F2}\u{1F1FE}" },
  { code: "RU", name: "Russia", flag: "\u{1F1F7}\u{1F1FA}" },
  { code: "UA", name: "Ukraine", flag: "\u{1F1FA}\u{1F1E6}" },
  { code: "IL", name: "Israel", flag: "\u{1F1EE}\u{1F1F1}" },
  { code: "AE", name: "United Arab Emirates", flag: "\u{1F1E6}\u{1F1EA}" },
  { code: "JP", name: "Japan", flag: "\u{1F1EF}\u{1F1F5}" },
  { code: "KR", name: "South Korea", flag: "\u{1F1F0}\u{1F1F7}" },
  { code: "NZ", name: "New Zealand", flag: "\u{1F1F3}\u{1F1FF}" },
  { code: "LU", name: "Luxembourg", flag: "\u{1F1F1}\u{1F1FA}" },
  { code: "MT", name: "Malta", flag: "\u{1F1F2}\u{1F1F9}" },
  { code: "BG", name: "Bulgaria", flag: "\u{1F1E7}\u{1F1EC}" },
  { code: "RS", name: "Serbia", flag: "\u{1F1F7}\u{1F1F8}" },
  { code: "SK", name: "Slovakia", flag: "\u{1F1F8}\u{1F1F0}" },
  { code: "LT", name: "Lithuania", flag: "\u{1F1F1}\u{1F1F9}" },
  { code: "LV", name: "Latvia", flag: "\u{1F1F1}\u{1F1FB}" },
  { code: "EE", name: "Estonia", flag: "\u{1F1EA}\u{1F1EA}" },
  { code: "IS", name: "Iceland", flag: "\u{1F1EE}\u{1F1F8}" },
  { code: "MK", name: "North Macedonia", flag: "\u{1F1F2}\u{1F1F0}" },
  { code: "BA", name: "Bosnia and Herzegovina", flag: "\u{1F1E7}\u{1F1E6}" },
  { code: "AL", name: "Albania", flag: "\u{1F1E6}\u{1F1F1}" },
  { code: "ME", name: "Montenegro", flag: "\u{1F1F2}\u{1F1EA}" },
].sort((a, b) => a.name.localeCompare(b.name));

const CODE_TO_COUNTRY = Object.fromEntries(ALL_COUNTRIES.map(c => [c.code, c]));

interface SeedRequestFormProps {
  prefillCity?: string;
  compact?: boolean;
}

interface GeocodeCityResult {
  countryCode: string | null;
  isValidCity: boolean;
  canonicalName: string | null;
}

interface GeocodeCityMultiResult {
  results: {
    countryCode: string;
    canonicalName: string;
  }[];
  isValidCity: boolean;
}

const VALID_PLACE_TYPES = new Set([
  "city", "town", "village", "municipality", "hamlet",
  "suburb", "borough", "quarter", "neighbourhood",
  "administrative",
]);

async function geocodeCityMulti(cityName: string): Promise<GeocodeCityMultiResult> {
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityName)}&format=json&limit=5&addressdetails=1`;
    const res = await fetch(url, {
      headers: { "User-Agent": "Inkvaluator/1.0 (inkvaluator.com)" },
    });
    if (!res.ok) return { results: [], isValidCity: false };
    const data = await res.json();
    if (!data?.length) return { results: [], isValidCity: false };

    const validResults: { countryCode: string; canonicalName: string }[] = [];
    const seenCountries = new Set<string>();

    for (const result of data) {
      const placeType = result.type || "";
      const category = result.class || "";
      const isPlace = category === "place" || category === "boundary";
      const isValidType = VALID_PLACE_TYPES.has(placeType) || placeType.includes("city") || placeType.includes("town");

      if (isPlace && isValidType) {
        const cc = result.address?.country_code?.toUpperCase();
        const name = result.address?.city || result.address?.town || result.address?.village || result.name;
        if (cc && name && !seenCountries.has(cc)) {
          seenCountries.add(cc);
          validResults.push({ countryCode: cc, canonicalName: name });
        }
      }
    }

    if (validResults.length > 0) {
      return { results: validResults, isValidCity: true };
    }

    // Fallback: check first result
    const first = data[0];
    const badTypes = ["highway", "road", "building", "shop", "amenity", "tourism", "leisure"];
    if (badTypes.includes(first.class)) {
      return { results: [], isValidCity: false };
    }

    const cityFromAddr = first.address?.city || first.address?.town || first.address?.village;
    const cc = first.address?.country_code?.toUpperCase();
    if (cityFromAddr && cc) {
      return { results: [{ countryCode: cc, canonicalName: cityFromAddr }], isValidCity: true };
    }

    return { results: [], isValidCity: false };
  } catch {
    return { results: [], isValidCity: false };
  }
}

// Legacy single-result wrapper
async function geocodeCity(cityName: string): Promise<GeocodeCityResult> {
  const multi = await geocodeCityMulti(cityName);
  if (multi.results.length > 0) {
    return {
      countryCode: multi.results[0].countryCode,
      isValidCity: multi.isValidCity,
      canonicalName: multi.results[0].canonicalName,
    };
  }
  return { countryCode: null, isValidCity: false, canonicalName: null };
}

export default function SeedRequestForm({ prefillCity, compact }: SeedRequestFormProps) {
  const geo = useGeoLocation();
  const [city, setCity] = useState(prefillCity || "");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validatedCity, setValidatedCity] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  // Multi-country disambiguation
  const [countryOptions, setCountryOptions] = useState<{ countryCode: string; canonicalName: string }[]>([]);
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const detectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: dbCountries = [] } = useQuery({
    queryKey: ["countries-active"],
    queryFn: async () => {
      const { data } = await supabase
        .from("countries")
        .select("code, name, flag_emoji")
        .eq("is_active", true)
        .order("name");
      return data || [];
    },
  });

  const dbCodes = new Set(dbCountries.map((c: any) => c.code));

  const mergedCountries = [
    ...dbCountries.map((c: any) => ({ code: c.code, name: c.name, flag: c.flag_emoji })),
    ...ALL_COUNTRIES.filter((c) => !dbCodes.has(c.code)),
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Sanitize city input on change
  const handleCityChange = (value: string) => {
    const sanitized = sanitizeCityName(value);
    setCity(sanitized);
  };

  // Sanitize & validate email
  const handleEmailChange = (value: string) => {
    const sanitized = sanitizeEmail(value);
    setEmail(sanitized);
    if (sanitized && !isValidEmail(sanitized)) {
      setEmailError("Ongeldig e-mailadres.");
    } else {
      setEmailError(null);
    }
  };

  useEffect(() => {
    if (!city.trim() || city.trim().length < 2) {
      setDetectedCountry(null);
      setValidationError(null);
      setValidatedCity(null);
      setCountryOptions([]);
      setShowCountryPicker(false);
      return;
    }

    // Client-side validation first
    const nameCheck = isValidCityName(city.trim());
    if (!nameCheck.valid) {
      setValidationError(nameCheck.reason || "Ongeldige stadsnaam.");
      setDetectedCountry(null);
      setValidatedCity(null);
      setCountryOptions([]);
      setShowCountryPicker(false);
      return;
    }

    if (detectTimeout.current) clearTimeout(detectTimeout.current);
    detectTimeout.current = setTimeout(async () => {
      setDetecting(true);
      setValidationError(null);
      setCountryOptions([]);
      setShowCountryPicker(false);

      const multiResult = await geocodeCityMulti(city.trim());
      setDetecting(false);

      if (multiResult.isValidCity && multiResult.results.length > 1) {
        // City exists in multiple countries — show picker
        setCountryOptions(multiResult.results);
        setShowCountryPicker(true);
        setValidatedCity(multiResult.results[0].canonicalName);
        // Don't auto-select country, let user choose
        if (!country) {
          setDetectedCountry(null);
        }
      } else if (multiResult.isValidCity && multiResult.results.length === 1) {
        // Single country — auto-detect as before
        const result = multiResult.results[0];
        setDetectedCountry(result.countryCode);
        setValidatedCity(result.canonicalName);
        if (!country) {
          setCountry(result.countryCode);
        }
      } else {
        setDetectedCountry(null);
        setValidatedCity(null);
        setValidationError("Stad niet gevonden. Controleer de spelling.");
      }
    }, 800);
    return () => { if (detectTimeout.current) clearTimeout(detectTimeout.current); };
  }, [city]);

  const effectiveCountry = country || detectedCountry || geo.countryCode || "";

  const handleCountrySelect = (code: string) => {
    setCountry(code);
    setShowCountryPicker(false);
    // Find the canonical name for the selected country
    const match = countryOptions.find(o => o.countryCode === code);
    if (match) {
      setValidatedCity(match.canonicalName);
      setDetectedCountry(code);
    }
  };

  const handleSubmit = async () => {
    if (!city.trim()) return;

    // Rate limiting
    if (!checkRateLimit('seed-request', 3)) {
      setValidationError("Te veel aanvragen. Probeer het over een minuut opnieuw.");
      return;
    }

    // Validate city name
    const nameCheck = isValidCityName(city.trim());
    if (!nameCheck.valid) {
      setValidationError(nameCheck.reason || "Ongeldige stadsnaam.");
      return;
    }

    // Validate email if provided
    if (email && !isValidEmail(email)) {
      setEmailError("Ongeldig e-mailadres.");
      return;
    }

    // If multi-country and no choice made
    if (countryOptions.length > 1 && !country) {
      setValidationError("Selecteer een land. Deze stad bestaat in meerdere landen.");
      setShowCountryPicker(true);
      return;
    }

    setLoading(true);

    // Validate city before submitting
    if (!validatedCity && !detecting) {
      const result = await geocodeCity(city.trim());
      if (!result.isValidCity) {
        setValidationError("Dit lijkt geen geldige stad te zijn. Probeer een andere naam.");
        setLoading(false);
        return;
      }
      setValidatedCity(result.canonicalName);
      if (result.countryCode) setDetectedCountry(result.countryCode);
    }

    let finalCountry = effectiveCountry || "NL";
    if (!country && !detectedCountry) {
      const result = await geocodeCity(city.trim());
      if (result.countryCode) finalCountry = result.countryCode;
    }

    if (!dbCodes.has(finalCountry)) {
      const info = CODE_TO_COUNTRY[finalCountry];
      await supabase.from("countries").upsert(
        {
          code: finalCountry,
          name: info?.name || finalCountry,
          flag_emoji: info?.flag || "\u{1F30D}",
          is_active: true,
          center_lat: 0,
          center_lng: 0,
          default_zoom: 7,
        } as any,
        { onConflict: "code" },
      );
    }

    const submitCityName = validatedCity || city.trim();

    await supabase.from("seed_requests").insert({
      city: submitCityName,
      requested_by_email: email.trim() || null,
      country: finalCountry,
    } as any);

    const cleanName = normalizeCityName(submitCityName);
    const slug = slugifyCity(cleanName);
    await supabase.from("city_seed_queue").upsert(
      {
        city: cleanName,
        city_slug: slug,
        country: finalCountry,
        priority: 1,
      } as any,
      { onConflict: "city_slug,country", ignoreDuplicates: true },
    );

    if (email.trim()) {
      supabase.functions
        .invoke("send-city-request-confirmation", {
          body: { email: email.trim(), city: city.trim(), country: finalCountry },
        })
        .catch(() => {});
    }

    setSubmitted(true);
    setLoading(false);
  };

  if (submitted) {
    const countryInfo = CODE_TO_COUNTRY[effectiveCountry || "NL"];
    return (
      <div
        className={
          compact
            ? "flex items-center gap-2 text-sm"
            : "rounded-xl border border-primary/20 bg-primary/5 p-6 text-center"
        }
      >
        <CheckCircle className={compact ? "w-4 h-4 text-primary" : "w-8 h-8 text-primary mx-auto mb-3"} />
        {compact ? (
          <span className="text-foreground">Aanvraag ontvangen voor {city}!</span>
        ) : (
          <>
            <h3 className="font-display text-lg text-foreground">Aanvraag ontvangen!</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {city}{countryInfo ? ` (${countryInfo.flag} ${countryInfo.name})` : ""} wordt toegevoegd.
              Meestal binnen 24 uur verwerkt.
            </p>
          </>
        )}
      </div>
    );
  }

  // Country disambiguation picker (shared between compact & full)
  const CountryDisambiguationPicker = () => {
    if (!showCountryPicker || countryOptions.length < 2) return null;
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5 space-y-1.5">
        <p className="text-xs font-medium text-foreground">
          Deze stad bestaat in meerdere landen. Welk land bedoel je?
        </p>
        <div className="flex flex-wrap gap-1.5">
          {countryOptions.map((opt) => {
            const info = CODE_TO_COUNTRY[opt.countryCode];
            const isSelected = country === opt.countryCode;
            return (
              <button
                key={opt.countryCode}
                onClick={() => handleCountrySelect(opt.countryCode)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border border-border hover:border-primary/50 text-foreground"
                }`}
              >
                {info?.flag || "\u{1F30D}"} {info?.name || opt.countryCode}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              value={city}
              onChange={(e) => handleCityChange(e.target.value)}
              placeholder="Plaatsnaam"
              className="bg-input pr-8"
              maxLength={80}
            />
            {detecting && (
              <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={loading || !city.trim() || !!validationError || detecting || !!emailError}
            size="sm"
            className="gap-1.5 shrink-0"
          >
            {loading && <Loader2 className="w-3 h-3 animate-spin" />}
            Aanvragen
          </Button>
        </div>
        {validationError && !detecting && (
          <p className="text-[11px] text-destructive flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {validationError}
          </p>
        )}
        {validatedCity && validatedCity !== city.trim() && !detecting && !showCountryPicker && (
          <p className="text-[11px] text-primary flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Gevonden als: {validatedCity}
          </p>
        )}
        <CountryDisambiguationPicker />
        {!showCountryPicker && detectedCountry && !loading && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {CODE_TO_COUNTRY[detectedCountry]?.flag} {CODE_TO_COUNTRY[detectedCountry]?.name || detectedCountry}
            <button
              onClick={() => { setDetectedCountry(null); setCountry(""); }}
              className="text-primary hover:underline ml-1"
            >
              Wijzig
            </button>
          </p>
        )}
        {!showCountryPicker && detectedCountry === null && countryOptions.length === 0 && city.trim().length >= 2 && !detecting && (
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="bg-input h-8 text-xs">
              <SelectValue placeholder="Selecteer land" />
            </SelectTrigger>
            <SelectContent>
              {mergedCountries.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.flag} {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card/30 p-6 space-y-4">
      <div>
        <h3 className="font-display text-lg text-foreground">Jouw stad niet gevonden?</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Vraag je stad aan en we voegen hem toe aan het platform.
        </p>
      </div>

      <div className="space-y-1.5">
        <div className="relative">
          <Input
            value={city}
            onChange={(e) => handleCityChange(e.target.value)}
            placeholder="Plaatsnaam (bijv. Paramaribo)"
            className="bg-input pr-9"
            maxLength={80}
          />
          {detecting && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
          )}
        </div>
        {!showCountryPicker && detectedCountry && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3 h-3 shrink-0" />
            Gevonden in{" "}
            <span className="font-medium text-foreground">
              {CODE_TO_COUNTRY[detectedCountry]?.flag} {CODE_TO_COUNTRY[detectedCountry]?.name || detectedCountry}
            </span>
            <span className="text-muted-foreground/50">&mdash; niet correct?</span>
          </p>
        )}
        {validationError && !detecting && (
          <p className="text-xs text-destructive flex items-center gap-1.5 mt-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {validationError}
          </p>
        )}
        {validatedCity && validatedCity !== city.trim() && !detecting && !showCountryPicker && (
          <p className="text-xs text-primary flex items-center gap-1.5 mt-1">
            <CheckCircle className="w-3 h-3 shrink-0" />
            Gevonden als: {validatedCity}
          </p>
        )}
      </div>

      <CountryDisambiguationPicker />

      {!showCountryPicker && (
        <Select value={country || detectedCountry || geo.countryCode || ""} onValueChange={setCountry}>
          <SelectTrigger className="bg-input">
            <SelectValue placeholder="Selecteer land" />
          </SelectTrigger>
          <SelectContent>
            {mergedCountries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.flag} {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="space-y-1">
        <Input
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="E-mail voor bevestiging (optioneel)"
          type="email"
          className="bg-input"
          maxLength={255}
        />
        {emailError && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {emailError}
          </p>
        )}
      </div>

      <Button onClick={handleSubmit} disabled={loading || !city.trim() || !!validationError || detecting || !!emailError} className="w-full gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        Aanvraag indienen
      </Button>
    </div>
  );
}
